import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowUpRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/toaster';
import {
  heroImage,
  aboutImages,
  cowsImage,
  marqueeWords,
  projects,
  aboutText,
  quote,
} from '../mock';

// ---- constants (no more magic numbers) ----
const REVEAL_FALLBACK_MS = 900;
const REVEAL_OBSERVER_OPTS = {
  threshold: 0.08,
  rootMargin: '0px 0px -5% 0px',
};

const AboutBadge = () => (
  <div className="circle-text absolute -top-14 left-1/2 -translate-x-1/2 lg:top-8 lg:left-8 lg:translate-x-0 z-10">
    <svg viewBox="0 0 200 200">
      <defs>
        <path
          id="circlePath"
          d="M 100,100 m -76,0 a 76,76 0 1,1 152,0 a 76,76 0 1,1 -152,0"
          fill="none"
        />
      </defs>
      <text fill="#2A2A1E" fontFamily="'Playfair Display', serif" fontSize="14" letterSpacing="4">
        <textPath href="#circlePath" startOffset="0">
          ABOUT ME • ABOUT ME • ABOUT ME •
        </textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-2 h-2 rounded-full bg-olive-deep" />
    </div>
  </div>
);

const Home = () => {
  const revealRefs = useRef([]);
  revealRefs.current = [];

  const addRef = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  // Mount-only — the refs don't need to be in deps since they're populated before commit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, REVEAL_OBSERVER_OPTS);
    revealRefs.current.forEach((el) => io.observe(el));
    // Fallback: ensure visibility even if observer misses.
    const fallbackTimer = setTimeout(() => {
      revealRefs.current.forEach((el) => el && el.classList.add('is-visible'));
    }, REVEAL_FALLBACK_MS);
    return () => {
      io.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, []);

  return (
    <div className="bg-cream text-ink">
      <Header transparent />

      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
        <img
          src={heroImage}
          alt="Rashmi on the road at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.72) saturate(0.95)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
        <div className="vignette absolute inset-0" />

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-display text-cream-soft text-shadow-deep leading-[0.95] tracking-[0.02em]">
            <span className="block text-[clamp(2.2rem,6.2vw,5.4rem)] uppercase">
              Visuals, Lifestyle, &amp; Stories
            </span>
            <span className="block font-script text-cream-soft/95 text-[clamp(3.2rem,9vw,7.5rem)] -mt-2 lg:-mt-6">
              From the Travel
            </span>
          </h1>

          <p className="mt-6 max-w-md font-serif italic text-cream-soft/90 text-lg text-shadow-soft">
            {"A visual mind trying to capture everything all at once."}
          </p>

          <a
            href="#about"
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center text-cream-soft hover:text-gold transition-colors group"
          >
            <span className="text-[11px] tracking-[0.36em] uppercase font-semibold mb-2">Explore</span>
            <ChevronDown size={22} strokeWidth={1.5} className="explore-arrow" />
          </a>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="relative bg-cream-soft py-24 lg:py-32 overflow-hidden">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left image */}
          <div ref={addRef} className="reveal lg:col-span-4">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <img
                src={aboutImages.left}
                alt="Etta with chickens at the barn"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Center text */}
          <div ref={addRef} className="reveal relative lg:col-span-4 text-center px-2 pt-14 lg:pt-10">
            <AboutBadge />
            <p className="font-script text-olive text-5xl md:text-6xl leading-none">
              {aboutText.welcome}
            </p>
            <h2 className="font-display text-olive-deep text-[clamp(2rem,4vw,3.4rem)] mt-3 tracking-tight">
              {aboutText.heading}
            </h2>
            <p className="font-serif text-ink text-lg leading-relaxed mt-6 max-w-md mx-auto">
              {aboutText.body}
            </p>
            <a href="#projects" className="btn-gold inline-block mt-8">
              {aboutText.buttonLabel}
            </a>
          </div>

          {/* Right image */}
          <div ref={addRef} className="reveal lg:col-span-4">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <img
                src={aboutImages.right}
                alt="Etta by the chicken coop"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE OVER COWS */}
      <section className="relative w-full h-[70svh] min-h-[480px] overflow-hidden">
        <img
          src={cowsImage}
          alt="Cows grazing in an open field"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-transparent to-black/25" />
        <div className="relative z-10 h-full flex items-start">
          <div className="max-w-[1480px] w-full mx-auto px-6 lg:px-12 pt-16 lg:pt-24">
            <p className="font-hand text-olive-deep text-[clamp(2rem,4vw,3.6rem)] leading-[1.15] max-w-xl drop-shadow-[0_1px_0_rgba(255,255,255,0.25)]">
              “{quote}”
            </p>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative bg-cream-soft border-y border-ink/10 overflow-hidden py-6">
        <div className="marquee-track whitespace-nowrap">
          {[...marqueeWords, ...marqueeWords, ...marqueeWords].map((w, i) => (
            <span
              key={`${w}-${i}`}
              className="font-display text-olive-deep text-[clamp(2rem,5vw,4.5rem)] px-10 tracking-wider"
            >
              {w} <span className="text-gold-deep">/</span>
            </span>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="bg-cream py-24 lg:py-32">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-script text-olive text-5xl leading-none">selected work</p>
              <h2 className="font-display text-ink text-[clamp(2.4rem,5vw,4rem)] mt-2 leading-tight">
                Projects &amp; Collaborations
              </h2>
            </div>
            <p className="font-serif italic text-ink/70 max-w-md text-lg">
              A growing collection of campaigns, editorials and stories — from the beaches to the studio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {projects.map((p, idx) => (
              <Link
                key={p.slug}
                to={`/projects/${p.slug}`}
                ref={addRef}
                className={`reveal pc-card group block ${idx % 3 === 1 ? 'lg:mt-20' : ''}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-ink/5">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="pc-img absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-cream-soft text-[10px] tracking-[0.28em] uppercase font-semibold">
                    <span>{p.year}</span>
                    <span className="opacity-80">N° {String(idx + 1).padStart(2, '0')}</span>
                  </div>
                </div>
                <div className="pt-5 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-ink text-2xl md:text-[26px] tracking-tight group-hover:text-olive-deep transition-colors">
                      {p.title}
                    </h3>
                    <p className="font-serif italic text-ink/70 text-base mt-1">{p.subtitle}</p>
                  </div>
                  <ArrowUpRight
                    size={22}
                    strokeWidth={1.5}
                    className="mt-1 text-ink/50 group-hover:text-olive-deep group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />

      <style>{`
        .reveal { opacity: 1; transform: translateY(0); transition: opacity .9s ease, transform .9s ease; }
        @media (min-width: 1024px) {
          .reveal { opacity: 0; transform: translateY(28px); }
          .reveal.is-visible { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Home;
